'use client';

import RecordedBroadcastProvider from '@/components/camera-broadcast/RecordedBroadcastProvider';
import { WorkerPushRegistrationSync } from '@/components/auth/WorkerPushRegistrationSync';
import { AlarmLayerPopup } from '@/components/realtime/AlarmLayerPopup';
import { IoTFieldDataProvider } from '@/domains/iot-v2/provider';
import { BoardAlarmRealtimeProvider } from '@/components/realtime/BoardAlarmRealtimeProvider';
import { ProductCommandToaster } from '@/components/widgets/shared/ProductCommandToaster';
import { SESSION_EXPIRED_EVENT_NAME, resetSessionExpiredNotification, shouldDeferSessionExpiry } from '@/shared/auth/session-expiration';
import { isTerminalAuthErrorCode } from '@/shared/auth/session-state';
import { isSignOutInProgress, signOutWithRefreshRevoke } from '@/shared/auth/sign-out';
import { useIsSafeonFlutterClient } from '@/shared/forced-mobile/ForcedMobileProvider';
import { UNAUTH_LANDING_PAGE_URL } from '@/shared/routes';
import { zIndex } from '@/shared/ui/z-index';
import { SessionProvider, useSession } from 'next-auth/react';
import { usePathname, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

const PUBLIC_AUTH_PATHS = new Set([UNAUTH_LANDING_PAGE_URL, '/signup', '/']);
const BACKEND_WS_CONNECTION_CHECK_INTERVAL_MS = 30000;
const BACKEND_WS_CLIENT_ID_STORAGE_KEY = 'sacon.backendWsClientId';
const BACKEND_WS_CLIENT_ID_PATTERN = /^[A-Za-z0-9_-]{8,128}$/;

/**
 * 인증 없이 접근 가능한 공개 화면인지 확인합니다.
 */
function isPublicAuthPath(pathname: string | null) {
  return pathname !== null && PUBLIC_AUTH_PATHS.has(pathname);
}

/**
 * 관리자 화면은 실시간 WebSocket 연결과 알람 레이어를 사용하지 않습니다.
 */
function isAdminPath(pathname: string | null) {
  return pathname === '/admin' || pathname?.startsWith('/admin/') === true;
}

function isWorkerPath(pathname: string | null) {
  return pathname === '/worker' || pathname?.startsWith('/worker/') === true;
}

/**
 * 실시간 연결과 알람 레이어를 비활성화해야 하는 화면인지 확인합니다.
 */
function isRealtimeDisabledPath(pathname: string | null) {
  return isPublicAuthPath(pathname) || isAdminPath(pathname) || isWorkerPath(pathname);
}

/**
 * 브라우저 탭별 WebSocket watcher 식별자를 생성하거나 재사용합니다.
 */
function getOrCreateBackendWsClientId() {
  if (typeof window === 'undefined') {
    return '';
  }

  try {
    const storedClientId = window.sessionStorage.getItem(BACKEND_WS_CLIENT_ID_STORAGE_KEY);
    if (storedClientId && BACKEND_WS_CLIENT_ID_PATTERN.test(storedClientId)) {
      return storedClientId;
    }

    const nextClientId = crypto.randomUUID().replaceAll('-', '_');
    window.sessionStorage.setItem(BACKEND_WS_CLIENT_ID_STORAGE_KEY, nextClientId);
    return nextClientId;
  } catch {
    return crypto.randomUUID().replaceAll('-', '_');
  }
}

/**
 * 로그인 페이지를 제외한 보호 화면에서 세션 만료를 감지하고 재로그인을 유도합니다.
 */
function SessionExpiryGuard() {
  const { data: session, status } = useSession();
  const pathname = usePathname();
  const hasHandledRef = useRef(false);

  /**
   * 세션 만료 알림을 한 번만 노출하고 로그인 화면으로 이동합니다.
   */
  const handleSessionExpired = useCallback(() => {
    if (isPublicAuthPath(pathname)) {
      return;
    }

    if (hasHandledRef.current || isSignOutInProgress()) {
      return;
    }

    if (shouldDeferSessionExpiry()) return;

    hasHandledRef.current = true;
    window.alert('로그인 세션이 만료되었습니다. 다시 로그인해주세요.');

    void signOutWithRefreshRevoke({ redirectTo: UNAUTH_LANDING_PAGE_URL });
  }, [pathname]);

  useEffect(() => {
    if (status === 'authenticated') {
      hasHandledRef.current = false;
      resetSessionExpiredNotification();

      if (session?.refreshState === 'terminal') {
        handleSessionExpired();
      }

      return;
    }

    if (status !== 'unauthenticated' || isPublicAuthPath(pathname)) {
      return;
    }

    const controller = new AbortController();

    void fetch('/api/auth/session-state', {
      cache: 'no-store',
      credentials: 'include',
      signal: controller.signal,
    })
      .then(async response => {
        if (response.status !== 401) {
          return;
        }

        const payload = (await response.json().catch(() => null)) as { error_code?: unknown } | null;
        if (isTerminalAuthErrorCode(payload?.error_code)) {
          handleSessionExpired();
        }
      })
      .catch(() => {
        // 상태 확인 자체가 실패한 경우 기존 세션 쿠키를 보존합니다.
      });

    return () => {
      controller.abort();
    };
  }, [handleSessionExpired, pathname, session?.refreshState, status]);

  useEffect(() => {
    /**
     * 공통 API 클라이언트가 감지한 401 만료 이벤트를 수신합니다.
     */
    const handleExpiredEvent = () => {
      handleSessionExpired();
    };

    window.addEventListener(SESSION_EXPIRED_EVENT_NAME, handleExpiredEvent);
    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT_NAME, handleExpiredEvent);
    };
  }, [handleSessionExpired]);

  return null;
}

/**
 * 로그인 완료 후 서버 측 WebSocket 연결을 시작하고 실패 상태를 사용자에게 안내합니다.
 */
function BackendWsConnectionGuard() {
  const { data: session, status } = useSession();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const failureStateRef = useRef<{
    connectionKey: string;
    message: string | null;
  } | null>(null);
  const [, refreshFailureState] = useState(0);
  const sessionFieldErpId = (session as { fieldErpId?: unknown } | null)?.fieldErpId;
  const fieldErpId = typeof sessionFieldErpId === 'string' ? sessionFieldErpId.trim() : '';
  const sessionFieldUid = (session as { fieldUid?: unknown } | null)?.fieldUid;
  const fieldUid = searchParams.get('fieldUid')?.trim() || searchParams.get('field_uid')?.trim() || (typeof sessionFieldUid === 'string' ? sessionFieldUid.trim() : '');
  const isConnectionEnabled = !isRealtimeDisabledPath(pathname) && status === 'authenticated';
  const isFieldErpIdMissing = isConnectionEnabled && !fieldUid && !fieldErpId;
  const missingFieldKey = isFieldErpIdMissing ? (pathname ?? '(unknown)') : null;
  const confirmedMissingFieldKeyRef = useRef<string | null>(null);
  const [, refreshMissingFieldConfirmation] = useState(0);
  const connectionKey = isConnectionEnabled && (fieldUid || fieldErpId) ? `${pathname ?? '(unknown)'}:${fieldUid || fieldErpId}` : null;
  const missingFieldMessage =
    missingFieldKey && confirmedMissingFieldKeyRef.current === missingFieldKey ? '로그인 세션에 현장 ERP ID가 없어 실시간 연결을 시작하지 못했습니다.' : null;
  const failureMessage = missingFieldMessage ?? (connectionKey && failureStateRef.current?.connectionKey === connectionKey ? failureStateRef.current.message : null);

  useEffect(() => {
    if (!missingFieldKey) {
      confirmedMissingFieldKeyRef.current = null;
      return;
    }

    confirmedMissingFieldKeyRef.current = null;
    const missingFieldTimer = window.setTimeout(() => {
      confirmedMissingFieldKeyRef.current = missingFieldKey;
      refreshMissingFieldConfirmation(revision => revision + 1);
    }, 0);
    return () => {
      window.clearTimeout(missingFieldTimer);
    };
  }, [missingFieldKey]);

  useEffect(() => {
    if (!connectionKey) {
      failureStateRef.current = null;
      return;
    }

    const controller = new AbortController();
    let isStopped = false;
    let isChecking = false;

    /**
     * 인증 세션이 유효한 동안 서버 측 WebSocket 연결과 백엔드 WS 인증을 보장합니다.
     */
    const ensureConnection = async () => {
      if (isStopped || isChecking) {
        return;
      }

      isChecking = true;

      try {
        const clientId = getOrCreateBackendWsClientId();
        const response = await fetch('/api/ws/connect', {
          body: JSON.stringify({ clientId, fieldUid }),
          cache: 'no-store',
          headers: { 'Content-Type': 'application/json' },
          method: 'POST',
          signal: controller.signal,
        });
        const payload = await response.json().catch(() => null);

        if (!response.ok || payload?.ok === false) {
          const message = typeof payload?.message === 'string' ? payload.message : '실시간 WebSocket 연결에 실패했습니다. 잠시 후 다시 로그인하거나 관리자에게 문의해 주세요.';
          failureStateRef.current = { connectionKey, message };
          refreshFailureState(revision => revision + 1);
          return;
        }

        failureStateRef.current = { connectionKey, message: null };
        refreshFailureState(revision => revision + 1);
      } catch (error) {
        if (controller.signal.aborted || isStopped) {
          return;
        }

        failureStateRef.current = {
          connectionKey,
          message: error instanceof Error ? error.message : '실시간 WebSocket 연결에 실패했습니다.',
        };
        refreshFailureState(revision => revision + 1);
      } finally {
        isChecking = false;
      }
    };

    void ensureConnection();

    const connectionCheckInterval = window.setInterval(() => {
      void ensureConnection();
    }, BACKEND_WS_CONNECTION_CHECK_INTERVAL_MS);

    return () => {
      isStopped = true;
      window.clearInterval(connectionCheckInterval);
      controller.abort();
      const clientId = getOrCreateBackendWsClientId();
      void fetch('/api/ws/disconnect', {
        body: JSON.stringify({ clientId, fieldUid }),
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
      }).catch(() => undefined);
    };
  }, [connectionKey, fieldUid]);

  if (!failureMessage) {
    return null;
  }

  return (
    <div
      className="fixed right-4 bottom-[calc(16px_+_var(--app-safe-bottom-offset,0px))] w-[min(88vw,320px)] rounded-[4px] border border-red-200 bg-white/95 px-3 py-2 shadow-lg backdrop-blur sm:right-6 sm:bottom-[calc(24px_+_var(--app-safe-bottom-offset,0px))]"
      role="status"
      aria-live="polite"
      style={{ zIndex: zIndex.realtimeNotification }}>
      <div className="flex items-center gap-2">
        <span className="h-2 w-2 shrink-0 rounded-full bg-red-600" aria-hidden="true" />
        <p className="min-w-0 text-xs leading-5 font-semibold text-red-700">실시간 연결 재시도 중</p>
      </div>
      <p className="mt-1 line-clamp-2 text-xs leading-5 text-slate-700">{failureMessage}</p>
    </div>
  );
}

/**
 * 로그인 해제 또는 공개 인증 화면 진입 시 서버 측 WebSocket 연결을 정리합니다.
 */
function BackendWsDisconnectGuard() {
  const { status } = useSession();
  const pathname = usePathname();
  const disconnectedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const shouldDisconnect = isPublicAuthPath(pathname) || status === 'unauthenticated';

    if (!shouldDisconnect) {
      disconnectedKeyRef.current = null;
      return;
    }

    const disconnectKey = `${pathname ?? '(unknown)'}:${status}`;
    if (disconnectedKeyRef.current === disconnectKey) {
      return;
    }

    disconnectedKeyRef.current = disconnectKey;
    const controller = new AbortController();

    /**
     * 인증 해제 또는 공개 화면 진입 시 현재 탭의 WebSocket watcher를 해제합니다.
     */
    const disconnectConnection = async () => {
      try {
        const clientId = getOrCreateBackendWsClientId();
        await fetch('/api/ws/disconnect', {
          body: JSON.stringify({ clientId }),
          cache: 'no-store',
          headers: { 'Content-Type': 'application/json' },
          method: 'POST',
          signal: controller.signal,
        });
      } catch {
        // 연결 종료 실패는 화면 전환을 막지 않으므로 사용자 알림 없이 무시합니다.
      }
    };

    void disconnectConnection();

    return () => {
      controller.abort();
    };
  }, [pathname, status]);

  return null;
}

/**
 * 공개 인증/관리자 화면을 제외한 업무 화면에서만 실시간 연결과 알람 레이어를 활성화합니다.
 */
function RealtimeGuards({ children }: { children: ReactNode }) {
  const { data: session, status } = useSession();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const enabled = status === 'authenticated' && !isRealtimeDisabledPath(pathname);
  const sessionFieldUid = (session as { fieldUid?: unknown } | null)?.fieldUid;
  const fieldUid = searchParams.get('fieldUid')?.trim() || searchParams.get('field_uid')?.trim() || (typeof sessionFieldUid === 'string' ? sessionFieldUid.trim() : '');

  return (
    <IoTFieldDataProvider fieldUid={enabled ? fieldUid : ''}>
      <BoardAlarmRealtimeProvider key={fieldUid || 'unscoped'} enabled={enabled} fieldUid={fieldUid}>
        {enabled ? (
          <>
            <BackendWsConnectionGuard />
            <AlarmLayerPopup />
            {/* 여러 CCTV의 결과 알림을 한 곳에서 쌓고 카드 레이아웃은 유지합니다. */}
            <ProductCommandToaster />
          </>
        ) : null}
        {children}
      </BoardAlarmRealtimeProvider>
    </IoTFieldDataProvider>
  );
}

function MobileLocationSessionSync() {
  const { data: session, status } = useSession();
  const isFlutterClient = useIsSafeonFlutterClient();
  const identity = session?.mobileAuth?.mobile_context;
  const contextKey = identity ? [identity.user_uid, identity.field_uid, identity.device_uid, identity.session_uid].join(':') : status;

  useEffect(() => {
    if (!isFlutterClient || status === 'loading') return;
    // Send only a change signal. Flutter independently queries the authenticated context.
    const notify = () => {
      const bridge = window.flutter_inappwebview;
      if (typeof bridge?.callHandler !== 'function') return;
      void bridge.callHandler('workerSessionChanged').catch(() => {
        console.warn('[mobile-location] 앱에 로그인 상태 변경을 전달하지 못했습니다.');
      });
    };
    window.addEventListener('flutterInAppWebViewPlatformReady', notify);
    notify();
    return () => window.removeEventListener('flutterInAppWebViewPlatformReady', notify);
  }, [contextKey, isFlutterClient, status]);

  return null;
}

export default function AuthProvider({ children }: { children: ReactNode }) {
  return (
    <SessionProvider refetchInterval={60} refetchOnWindowFocus>
      <MobileLocationSessionSync />
      <WorkerPushRegistrationSync />
      <SessionExpiryGuard />
      <BackendWsDisconnectGuard />
      <RecordedBroadcastProvider>
        <RealtimeGuards>{children}</RealtimeGuards>
      </RecordedBroadcastProvider>
    </SessionProvider>
  );
}
